import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { eventStageTone } from '@/lib/event-status';
import { formatEventDate } from '@/lib/event-time';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';

/**
 * A role-scoped events table for the directory pages (city, state, venue,
 * account). The filters ride WITH the server's role scoping — everyone sees
 * only the events they're associated with under the existing rules.
 */

export interface EventsFilter {
  readonly city?: string;
  readonly usState?: string;
  readonly venue?: string;
  readonly accountId?: string;
  /** upcoming | past — the same state filter the events list uses. */
  readonly state?: 'upcoming' | 'past';
  /** One calendar day, YYYY-MM-DD. */
  readonly onDate?: string;
}

interface EventRow {
  readonly _id: string;
  readonly opportunityName?: string;
  readonly contractNumber?: string;
  readonly stageName?: string;
  readonly eventDate?: string;
  readonly timeStart?: string;
  readonly timeZone?: string;
  readonly longTimeZone?: string;
  readonly startVenue?: { readonly timezone?: { readonly id?: string } };
  readonly accountName?: string;
  readonly accountSalesforceId?: string | null;
  readonly location?: string;
  readonly programDisplayName?: string;
  readonly programName?: string;
}

const EVENT_TABLE_SORT_KEYS = ['display', 'accountName', 'eventDate', 'location', 'stageName'];

/** The row fields the search box matches. */
const EVENT_TABLE_SEARCH_FIELDS: ReadonlyArray<keyof EventTableRow> = ['display', 'accountName', 'location', 'contractNumber'];

/** An event with the name its first column shows, for search and sort. */
interface EventTableRow extends EventRow {
  readonly display: string;
}

/**
 * The search is local state, not `?q=`: a page can show several of these
 * tables at once (an account's upcoming and past events), each searched on
 * its own.
 */
export function EventsTable({ filter }: { readonly filter: EventsFilter }): React.ReactElement {
  const { sort, onSort } = useTableSort(EVENT_TABLE_SORT_KEYS);
  const [search, setSearch] = useState('');
  const events = useQuery({
    queryKey: ['events-for', filter],
    queryFn: ({ signal }) =>
      getJson<EventRow[]>(
        '/events',
        {
          ...filter,
          limit: 100,
          sortField: 'eventDate',
          sortDirection: filter.state === 'upcoming' ? 'asc' : 'desc',
        },
        signal,
      ),
  });

  if (events.isPending) return <SkeletonTable label="Loading events…" />;
  if (events.isError) {
    return <ErrorNotice title="Events unavailable" message={errorMessage(events.error)} />;
  }
  if (events.data.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<CalendarIcon aria-hidden className="size-6" />}
          title="No events you can see here"
          message="Either nothing matches, or the matching events aren't ones you're associated with."
        />
      </Panel>
    );
  }
  // The list arrives fully loaded (≤100 rows) — sort client-side, keyed on
  // the same display value each column shows.
  const rows = sortRows(
    filterRows<EventTableRow>(
      events.data.map((event) => ({
        ...event,
        display: event.programDisplayName || event.programName || event.opportunityName || '',
      })),
      search,
      EVENT_TABLE_SEARCH_FIELDS,
    ),
    sort,
  );
  return (
    <>
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <SearchInput
        label="Search these events"
        placeholder="Event, client, location or contract"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="w-full max-w-sm"
      />
    </div>
    {rows.length === 0 ? (
      <Panel>
        <EmptyState title="No events match" message="Try a different search." />
      </Panel>
    ) : (
    <DataTable
      headers={[
        { label: 'Event', sortKey: 'display' },
        { label: 'Client', sortKey: 'accountName' },
        { label: 'Date', sortKey: 'eventDate' },
        { label: 'Location', sortKey: 'location' },
        { label: 'Stage', sortKey: 'stageName' },
      ]}
      minWidth={860}
      {...(sort ? { sort } : {})}
      onSort={onSort}
    >
      {rows.map((event) => (
        <tr key={event._id} className="border-t border-portal-line align-top">
          <td className="p-3">
            <Link
              to={`/events/${event._id}`}
              className="text-portal-small font-bold text-brand hover:underline"
            >
              {event.programDisplayName || event.programName || event.opportunityName || 'Event'}
            </Link>
            {event.contractNumber ? (
              <span className="block font-mono text-portal-micro text-portal-muted">
                {event.contractNumber}
              </span>
            ) : null}
          </td>
          <td className="p-3 text-portal-small">
            {event.accountName && event.accountSalesforceId ? (
              <Link to={`/accounts/${event.accountSalesforceId}`} className="text-brand hover:underline">
                {event.accountName}
              </Link>
            ) : (
              <span className="text-portal-muted">{event.accountName || '—'}</span>
            )}
          </td>
          <td className="p-3 whitespace-nowrap text-portal-small">
            {event.eventDate ? (
              <Link
                to={`/places/events?onDate=${event.eventDate.slice(0, 10)}`}
                title="See everything on this date"
                className="text-brand hover:underline"
              >
                {formatEventDate(event, 'medium')}
              </Link>
            ) : (
              <span className="text-portal-muted">—</span>
            )}
          </td>
          <td className="p-3 text-portal-small text-portal-muted">{event.location || '—'}</td>
          <td className="p-3 whitespace-nowrap">
            {event.stageName ? (
              <Pill tone={eventStageTone(event.stageName)}>{event.stageName}</Pill>
            ) : (
              <span className="text-portal-small text-portal-muted">—</span>
            )}
          </td>
        </tr>
      ))}
    </DataTable>
    )}
    </>
  );
}
